import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { itemsApi, projectsApi, listingsApi } from '../utils/api';
import { Spinner } from '../components/shared/Spinner';
import { ConfirmationModal } from '../components/shared/ConfirmationModal';
import { ItemDetailModal } from '../components/shared/ItemDetailModal';
import { AITestItemsButton } from '../components/shared/AITestItemsButton';
import { ProjectSelectorModal } from '../components/shared/ProjectSelectorModal';
import { useViewMode } from '../hooks/useViewMode';

interface Item {
  _id: string;
  title: string;
  description: string;
  images: string[];
  condition: 'new' | 'used' | 'salvage';
  price: number;
  currency: string;
  materials: string[];
  dimensions?: {
    width: number;
    height: number;
    depth: number;
    units: string;
  };
  project?: {
    _id: string;
    title: string;
  };
  status: 'draft' | 'listed';
  aiAnalysis?: {
    estimatedValue: number;
    confidence: number;
    notes: string;
    recommendedTitle: string;
    attributes: string[];
    reasoning: string;
  };
  createdAt: string;
  updatedAt: string;
}

export const Items: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [items, setItems] = useState<Item[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useViewMode('grid');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [bulkListingLoading, setBulkListingLoading] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      // Only load items for the current user
      const response = await itemsApi.list({ seller: user?.id });
      
      if (response.data.success) {
        setItems(response.data.data || []);
      } else {
        console.error('Failed to load items');
        setItems([]);
      }
    } catch (error: any) {
      console.error('Failed to load items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadItems();
    }
  }, [isAuthenticated, user, loadItems]);

  const handleItemSelect = (itemId: string) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(itemId)) {
      newSelection.delete(itemId);
    } else {
      newSelection.add(itemId);
    }
    setSelectedItems(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(item => item._id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedItems.size === 0) return;
    setShowDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    setShowDeleteConfirm(false);
    setDeleting(true);
    
    try {
      const selectedItemsArray = Array.from(selectedItems);
      const promises = selectedItemsArray.map(async (itemId) => {
        return itemsApi.delete(itemId);
      });

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r?.data.success).length;
      
      if (successCount > 0) {
        // Reload items from server to get updated list
        await loadItems();
        setSelectedItems(new Set());
      }
      
      if (successCount === 0) {
        alert('Failed to delete any items. Please try again.');
      } else if (successCount < selectedItemsArray.length) {
        alert(`Deleted ${successCount} item${successCount !== 1 ? 's' : ''} but ${selectedItemsArray.length - successCount} failed.`);
      }
      
    } catch (error: any) {
      console.error('Failed to delete items:', error);
      alert('Failed to delete some items. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const cancelBulkDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleItemClick = (item: Item) => {
    setSelectedItem(item);
    setShowItemModal(true);
  };

  const handleCloseItemModal = () => {
    setShowItemModal(false);
    setSelectedItem(null);
  };

  const handleItemUpdated = (updatedItem: Item) => {
    // Update the item in the items array
    setItems(prevItems => 
      prevItems.map(item => 
        item._id === updatedItem._id ? updatedItem : item
      )
    );
    // Update the selected item
    setSelectedItem(updatedItem);
  };

  const handleBulkAddToProject = () => {
    if (selectedItems.size === 0) return;
    setShowProjectSelector(true);
  };

  const handleSelectProject = async (projectId: string, projectName: string) => {
    setShowProjectSelector(false);
    
    try {
      const selectedItemsArray = Array.from(selectedItems);
      const promises = selectedItemsArray.map(async (itemId) => {
        return itemsApi.update(itemId, { project: projectId });
      });

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r?.data.success).length;
      
      if (successCount > 0) {
        // Reload items from server to get updated list
        await loadItems();
        setSelectedItems(new Set());
      }
      
      if (successCount === 0) {
        alert('Failed to add any items to project. Please try again.');
      } else if (successCount < selectedItemsArray.length) {
        alert(`Added ${successCount} item${successCount !== 1 ? 's' : ''} to project but ${selectedItemsArray.length - successCount} failed.`);
      }
      
    } catch (error: any) {
      console.error('Failed to add items to project:', error);
      alert('Failed to add some items to project. Please try again.');
    }
  };

  const handleBulkPostToListings = async () => {
    if (selectedItems.size === 0) return;

    setBulkListingLoading(true);
    try {
      const selectedItemsArray = Array.from(selectedItems);
      const promises = selectedItemsArray.map(async (itemId) => {
        const item = items.find(i => i._id === itemId);
        if (!item) return null;

        // Create listing from item
        const listingData = {
          title: item.title,
          description: item.description || 'No description available',
          price: item.price,
          condition: item.condition.charAt(0).toUpperCase() + item.condition.slice(1), // Capitalize
          images: item.images,
          project: item.project?._id,
          category: 'Other', // Default category - could be improved with AI categorization
          location: {
            address: '123 Main Street', // Default address - should be from user profile
            city: 'Auckland',
            region: 'Auckland',
            country: 'New Zealand'
          },
          quantity: 1,
          availableQuantity: 1,
          tags: item.materials || []
        };

        return listingsApi.create(listingData);
      });

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r?.data.success).length;
      
      if (successCount > 0) {
        // Update item status to 'listed' for successful listings
        const successfulItems = selectedItemsArray.slice(0, successCount);
        const updatePromises = successfulItems.map(itemId => 
          itemsApi.update(itemId, { status: 'listed' })
        );
        await Promise.all(updatePromises);
        
        // Reload items and clear selection
        await loadItems();
        setSelectedItems(new Set());
      }
      
      if (successCount === 0) {
        alert('Failed to post any items to listings. Please try again.');
      } else if (successCount < selectedItemsArray.length) {
        alert(`Posted ${successCount} item${successCount !== 1 ? 's' : ''} to listings but ${selectedItemsArray.length - successCount} failed.`);
      }
      
    } catch (error: any) {
      console.error('Failed to post items to listings:', error);
      alert('Failed to post some items to listings. Please try again.');
    } finally {
      setBulkListingLoading(false);
    }
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'listed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'draft':
        return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300';
      default:
        return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300';
    }
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'new':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'used':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'salvage':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      default:
        return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {/* Count removed as requested */}
        </div>

        <div className="flex items-center space-x-3">
          {/* AI Test Button */}
          <AITestItemsButton 
            onItemsGenerated={loadItems}
            className=""
          />

          {/* New Item Button */}
          <Link 
            to="/marketplace/create" 
            className="btn-primary p-2"
            title="New Item"
            data-tutorial="new-item"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </Link>

          {/* View Mode Toggle */}
          <div className="flex items-center space-x-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-slate-700 shadow-sm'
                  : 'hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-slate-700 shadow-sm'
                  : 'hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Select All Button (only show when items are selected) */}
          {selectedItems.size > 0 && (
            <>
              <button
                onClick={handleSelectAll}
                className="btn-secondary"
              >
                {selectedItems.size === items.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={() => {
                  setSelectedItems(new Set());
                }}
                className="btn-secondary"
              >
                Clear Selection
              </button>
            </>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedItems.size > 0 && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBulkAddToProject}
                className="btn-secondary text-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add to Project
              </button>
              <button
                onClick={handleBulkPostToListings}
                disabled={bulkListingLoading}
                className="btn-primary text-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                {bulkListingLoading ? 'Posting...' : 'Post to Listings'}
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={deleting}
                className="btn-destructive text-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Items List */}
      {items.length === 0 ? (
        <div className="text-center py-12">
          <svg className="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">No items yet</h3>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            Create items from your projects or photos to see them here.
          </p>
          <div className="flex justify-center space-x-3">
            <Link to="/projects" className="btn-primary">
              Go to Projects
            </Link>
            <Link to="/photos" className="btn-secondary">
              Go to Photos
            </Link>
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map((item) => {
            const isSelected = selectedItems.has(item._id);
            const aiValue = item.aiAnalysis?.estimatedValue;
            
            return (
              <div
                key={item._id}
                className={`bg-white dark:bg-slate-800 rounded-xl shadow-sm border transition-all cursor-pointer ${
                  isSelected 
                    ? 'border-primary-500 ring-2 ring-primary-500/20' 
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                }`}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  const isCheckboxClick = target.closest('.checkbox-area');
                  
                  if (isCheckboxClick) {
                    handleItemSelect(item._id);
                  } else {
                    handleItemClick(item);
                  }
                }}
              >
                {/* Selection Checkbox */}
                <div className={`checkbox-area absolute top-3 left-3 w-5 h-5 rounded border-2 flex items-center justify-center z-10 ${
                  isSelected 
                    ? 'bg-primary-500 border-primary-500' 
                    : 'bg-white border-slate-300 dark:border-slate-600'
                }`}>
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>

                {/* Item Image */}
                <div className="relative h-48 bg-slate-100 dark:bg-slate-700 rounded-t-xl overflow-hidden">
                  {item.images && item.images.length > 0 ? (
                    <img
                      src={item.images[0]}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : null}
                  {/* Fallback icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                </div>

                {/* Item Details */}
                <div className="p-4">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2 line-clamp-2">
                    {item.title}
                  </h3>
                  
                  {item.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-3 line-clamp-2">
                      {item.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${getConditionColor(item.condition)}`}>
                        {item.condition}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      {aiValue && (
                        <div className="text-xs text-blue-500 dark:text-blue-400">
                          AI: ${aiValue.toFixed(2)}
                        </div>
                      )}
                      <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        ${item.price.toFixed(2)}
                      </div>
                    </div>
                    
                    {item.project && (
                      <Link
                        to={`/projects/${item.project._id}`}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.project.title}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const isSelected = selectedItems.has(item._id);
            const aiValue = item.aiAnalysis?.estimatedValue;
            
            return (
              <div
                key={item._id}
                className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
                  isSelected
                    ? 'border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                }`}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  const isCheckboxClick = target.closest('.checkbox-area');
                  
                  if (isCheckboxClick) {
                    handleItemSelect(item._id);
                  } else {
                    handleItemClick(item);
                  }
                }}
              >
                {/* Selection Checkbox */}
                <div className={`checkbox-area w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 shadow-sm ${
                  isSelected
                    ? 'bg-primary-500 border-primary-500 scale-110'
                    : 'bg-white border-slate-300 dark:border-slate-600 dark:bg-slate-700 hover:scale-105'
                }`}>
                  {isSelected && (
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>

                {/* Thumbnail */}
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-700 flex-shrink-0">
                  {item.images && item.images.length > 0 ? (
                    <img
                      src={item.images[0]}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-900 dark:text-slate-100 truncate">
                    {item.title}
                  </h3>
                  {item.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                      {item.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${getConditionColor(item.condition)}`}>
                        {item.condition}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                    </div>
                    <div className="text-right">
                      {aiValue && (
                        <div className="text-xs text-blue-500 dark:text-blue-400">
                          AI: ${aiValue.toFixed(2)}
                        </div>
                      )}
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        ${item.price.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  {item.project && (
                    <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
                      {item.project.title}
                    </p>
                  )}
                  {item.materials && item.materials.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.materials.slice(0, 3).map((material) => (
                        <span
                          key={material}
                          className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded"
                        >
                          {material}
                        </span>
                      ))}
                      {item.materials.length > 3 && (
                        <span className="px-2 py-1 text-xs text-slate-500">
                          +{item.materials.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Delete Items"
        message={`Are you sure you want to delete ${selectedItems.size} item${selectedItems.size !== 1 ? 's' : ''}? This action cannot be undone and will permanently remove the selected items.`}
        confirmText={`Delete ${selectedItems.size} Item${selectedItems.size !== 1 ? 's' : ''}`}
        cancelText="Cancel"
        onConfirm={confirmBulkDelete}
        onCancel={cancelBulkDelete}
        isLoading={deleting}
        variant="danger"
      />

      {/* Item Detail Modal */}
      <ItemDetailModal
        isOpen={showItemModal}
        onClose={handleCloseItemModal}
        item={selectedItem}
        onItemUpdated={handleItemUpdated}
      />

      {/* Project Selector Modal */}
      <ProjectSelectorModal
        isOpen={showProjectSelector}
        onClose={() => setShowProjectSelector(false)}
        onSelectProject={handleSelectProject}
        selectedCount={selectedItems.size}
        itemType="items"
      />
    </div>
  );
};
